/**
 * useSyncActiveOrgFromUrl — one-way sync from the URL to Redux/cookie.
 *
 * Mounted at the shell level (AppShell) so every /o/:slug/… route seen
 * by the browser is reflected in the switcher + the X-Active-Org header
 * apiFetch attaches to outgoing requests. Fixes the class of bugs where:
 *   - Landing on /o/orgA/... via deep link shows orgB in the switcher.
 *   - Back/forward navigation between orgs leaves the switcher stale.
 *   - The cookie / X-Active-Org header trails behind the URL, so /api
 *     requests target the wrong tenant.
 *
 * Does NOT mutate the URL — the router owns it. It only pushes the URL's
 * slug into Redux + the activeOrg cookie via useOrgs.switchOrg. Silent
 * no-op if the URL has no slug OR the slug isn't in the caller's org list
 * (guarded so a rogue link can't force-switch to a stranger's org).
 */
import { useEffect } from 'react';

import { useOrgs } from '@/features/org/hooks/use-orgs/useOrgs';
import { useLocation } from '@/shared/hooks/use-router/react-router-hooks';

/** Extract :slug from /o/:slug/... — returns null on any other path. */
function slugFromPath(pathname: string): string | null {
  const m = pathname.match(/^\/o\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function useSyncActiveOrgFromUrl(): void {
  const location = useLocation();
  const { orgs, activeSlug, switchOrg } = useOrgs();

  useEffect(() => {
    const urlSlug = slugFromPath(location.pathname);
    if (!urlSlug || urlSlug === activeSlug) return;
    // Only accept the URL's slug if it's in the caller's membership list.
    // Guards against a shared link to an org the user isn't in — the
    // switcher would otherwise display a phantom name.
    if (orgs.length === 0) return; // still loading — retry on next render
    if (!orgs.some((o) => o.slug === urlSlug)) return;
    switchOrg(urlSlug);
  }, [location.pathname, orgs, activeSlug, switchOrg]);
}
